import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";

import { getSuburbInsights } from "../api/prospectingReportsApi";
import { PROPERTY_TYPES, SALE_METHODS } from "../cmaReportTypes";

const input = "w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white";
const pad = (n) => String(n).padStart(2, "0");
const localIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const Section = ({ title, note, error, children }) => (
  <section className="pb-5 border-b border-gray-100">
    <h3 className="text-base font-bold text-[#4b2a83] mb-1">{title}</h3>
    {note && <p className="text-xs text-gray-500 mb-3">{note}</p>}
    {children}
    {error && <p className="text-sm text-red-600 mt-2" role="alert">{error}</p>}
  </section>
);

const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const num = (v) => (v === "" || v == null ? null : Number(v));

/** Live version of the backend's 10% rule, so the agent sees it before publishing. */
export const rangeProblem = (low, high) => {
  if (low == null || high == null || low === "" || high === "") return "";
  if (Number(high) <= Number(low)) return "The top of the range must be above the bottom.";
  const cap = Math.floor(Number(low) * 1.1);
  if (Number(high) > cap) return `Too wide. The top can be at most $${cap.toLocaleString("en-AU")} (10% above the lower price).`;
  return "";
};

/**
 * The Property Price Statement form, the sections of the statutory form in
 * the order Property Data publishes them. `form` holds the report fields the
 * statement prints; `onChange` takes a partial update. `errors` are the
 * backend's publish errors, keyed by field.
 */
const PriceStatementForm = ({ report, form, onChange, comparablesCount, errors = {} }) => {
  const statement = form.statement || {};
  const median = statement.median || {};
  const [loadingMedian, setLoadingMedian] = useState(false);
  const setStatement = (patch) => onChange({ statement: { ...statement, ...patch } });
  const setMedian = (patch) => setStatement({ median: { ...median, ...patch } });
  const single = statement.price_type === "single";
  const auctionish = statement.sale_method === "auction" || statement.sale_method === "fixed_date";

  const fillMedian = async () => {
    if (!report?.suburb) return;
    setLoadingMedian(true);
    try {
      const { data } = await getSuburbInsights({
        suburb: report.suburb, state: report.state || "", postcode: report.postcode || "",
        property_type: form.property_type || "",
      });
      const headline = data?.headline || data || {};
      const to = new Date();
      const from = new Date(to);
      from.setMonth(from.getMonth() - 12);
      setMedian({
        price: headline.median_sale_price ?? median.price ?? "",
        property_type: form.property_type || median.property_type || "house",
        suburb: report.suburb,
        period_from: median.period_from || localIso(from),
        period_to: median.period_to || localIso(to),
        source: headline.median_sale_price_source === "homer" ? "Homer market feed" : "Collings sales data",
      });
    } catch (error) {
      /* the agent can type the median in */
    } finally {
      setLoadingMedian(false);
    }
  };

  // First open: fill the median from our data so the agent only checks it.
  useEffect(() => {
    if (!median.price && report?.suburb) fillMedian();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const problem = single ? "" : rangeProblem(form.sale_estimate_min, form.sale_estimate_max);

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-[#4b2a83]">Property Price Statement</h2>
        <p className="text-xs text-gray-500">Section 47AF of the Estate Agents Act 1980</p>
      </div>

      <Section title="Sale" error={errors.sale_method || errors.location}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field id="pps-location" label="Property location">
            <select id="pps-location" className={input} value={statement.location || "metro"}
              onChange={(e) => setStatement({ location: e.target.value })}>
              <option value="metro">Metropolitan Melbourne</option>
              <option value="regional">Outside Metropolitan Melbourne</option>
            </select>
          </Field>
          <Field id="pps-sale-method" label="Method of sale">
            <select id="pps-sale-method" className={input} value={statement.sale_method || ""}
              onChange={(e) => setStatement({ sale_method: e.target.value })}>
              <option value="">Choose...</option>
              {SALE_METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Key features of the property">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field id="pps-type" label="Building type">
            <select id="pps-type" className={input} value={form.property_type || ""}
              onChange={(e) => onChange({ property_type: e.target.value })}>
              <option value="">N/A</option>
              {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          {[["bedrooms", "# of bedrooms"], ["bathrooms", "# of bathrooms"], ["car_spaces", "# of car spaces"], ["land_size", "Total land size (m2)"]].map(([key, label]) => (
            <Field key={key} id={`pps-${key}`} label={label}>
              <input id={`pps-${key}`} type="number" min="0" className={input} value={form[key] ?? ""}
                onChange={(e) => onChange({ [key]: num(e.target.value) })} />
            </Field>
          ))}
          <Field id="pps-floor" label="Internal floor space (m2)">
            <input id="pps-floor" type="number" min="0" className={input} value={statement.internal_floor_area ?? ""}
              onChange={(e) => setStatement({ internal_floor_area: e.target.value })} />
          </Field>
        </div>
      </Section>

      <Section
        title="Indicative selling price"
        note="An indicative selling price may be a single price or a range of not more than 10% of the value of the lower limit."
        error={errors.indicative_price || problem}
      >
        <div className="flex flex-wrap gap-4 mb-3 text-sm">
          {[["range", "Range between"], ["single", "Single price"]].map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
              <input type="radio" name="pps-price-type" checked={(statement.price_type || "range") === key}
                onChange={() => setStatement({ price_type: key })} />
              {label}
            </label>
          ))}
        </div>
        {single ? (
          <div className="max-w-xs">
            <Field id="pps-single" label="Single price ($)">
              <input id="pps-single" type="number" min="0" className={input} value={form.sale_estimate_min ?? ""}
                onChange={(e) => onChange({ sale_estimate_min: num(e.target.value), sale_estimate_max: num(e.target.value) })} />
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <Field id="pps-low" label="From ($)">
              <input id="pps-low" type="number" min="0" className={input} value={form.sale_estimate_min ?? ""}
                onChange={(e) => onChange({ sale_estimate_min: num(e.target.value) })} />
            </Field>
            <Field id="pps-high" label="To ($)">
              <input id="pps-high" type="number" min="0" className={input} value={form.sale_estimate_max ?? ""}
                onChange={(e) => onChange({ sale_estimate_max: num(e.target.value) })} />
            </Field>
          </div>
        )}
      </Section>

      <Section
        title="Seller's reserve price (if property is to be sold by auction or fixed-date sale)"
        note="Publish the reserve at least seven days before the auction or fixed-date sale."
      >
        <div className="max-w-xs">
          <Field id="pps-reserve" label="Seller's reserve price ($)">
            <input id="pps-reserve" type="number" min="0" className={input} disabled={!auctionish}
              placeholder={auctionish ? "" : "Not required for a private sale"}
              value={auctionish ? (statement.reserve_price ?? "") : ""}
              onChange={(e) => setStatement({ reserve_price: e.target.value })} />
          </Field>
        </div>
      </Section>

      <Section
        title="Median sale price"
        note="Residential sales in the same suburb, over a 3 to 12 month period ending no more than 6 months before this statement."
        error={errors.median}
      >
        <div className="flex justify-end -mt-2 mb-2">
          <button type="button" onClick={fillMedian} disabled={loadingMedian}
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900">
            {loadingMedian ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Fill from our sales data
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field id="pps-median" label="Median price ($)">
            <input id="pps-median" type="number" min="0" className={input} value={median.price ?? ""}
              onChange={(e) => setMedian({ price: e.target.value })} />
          </Field>
          <Field id="pps-median-type" label="Property type">
            <input id="pps-median-type" type="text" className={input} value={median.property_type || ""}
              onChange={(e) => setMedian({ property_type: e.target.value })} />
          </Field>
          <Field id="pps-median-suburb" label="Suburb">
            <input id="pps-median-suburb" type="text" className={input} value={median.suburb || report?.suburb || ""}
              onChange={(e) => setMedian({ suburb: e.target.value })} />
          </Field>
          <Field id="pps-median-from" label="Period from">
            <input id="pps-median-from" type="date" className={input} value={median.period_from || ""}
              onChange={(e) => setMedian({ period_from: e.target.value })} />
          </Field>
          <Field id="pps-median-to" label="to">
            <input id="pps-median-to" type="date" className={input} value={median.period_to || ""}
              onChange={(e) => setMedian({ period_to: e.target.value })} />
          </Field>
          <Field id="pps-median-source" label="Source">
            <input id="pps-median-source" type="text" className={input} value={median.source || ""}
              onChange={(e) => setMedian({ source: e.target.value })} />
          </Field>
        </div>
      </Section>

      <Section title="Comparable property sales" error={errors.comparables}>
        <p className="text-sm text-gray-700">
          {comparablesCount} of 3 comparable sales selected on the previous step.
        </p>
      </Section>

      {comparablesCount < 3 && (
        <Section
          title="Reason there are fewer than three comparable properties"
          note="Explain why you believe fewer than three comparable properties sold within two kilometres in the last six months."
          error={errors.fewer_reason}
        >
          <textarea id="pps-reason" rows={3} aria-label="Reason there are fewer than three comparable properties"
            className={input} value={statement.fewer_reason || ""}
            onChange={(e) => setStatement({ fewer_reason: e.target.value })} />
        </Section>
      )}

      <Section
        title="Sale outcome"
        note="Add the sale price within seven days after the property sells unconditionally. You can come back and fill this in later."
      >
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <Field id="pps-sold-price" label="Sale price ($)">
            <input id="pps-sold-price" type="number" min="0" className={input} value={statement.sold_price ?? ""}
              onChange={(e) => setStatement({ sold_price: e.target.value })} />
          </Field>
          <Field id="pps-sold-date" label="Unconditional sale date">
            <input id="pps-sold-date" type="date" className={input} value={statement.sold_date || ""}
              onChange={(e) => setStatement({ sold_date: e.target.value })} />
          </Field>
        </div>
      </Section>
    </div>
  );
};

export default PriceStatementForm;
